import { useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { getCurrencyMeta } from '../../constants/currencies';
import { getOperationPricing, PRICE_CURRENCIES } from '../../utils/operationPricing';
import { formatCurrency } from '../../utils/formatters';
import { toast } from 'react-hot-toast';
import useOperationStore from '../../store/useOperationStore';

const inputClass = 'mt-2 w-full rounded-xl border border-slate-200 bg-white p-3';

const currencyLabels = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£'
};

const formatMoney = (value, currency) => formatCurrency(value, currency);

const OperationSurveyPricingCard = ({ operation, canWrite }) => {
  const pricing = useMemo(() => getOperationPricing(operation), [operation]);
  const [surveyorName, setSurveyorName] = useState(operation.surveyorName || '');
  const [estimatedVolume, setEstimatedVolume] = useState(String(pricing.estimatedVolume || 0));
  const [pricingType, setPricingType] = useState(pricing.pricingType || 'PER_M3');
  const [currency, setCurrency] = useState(pricing.currency || 'TRY');
  const [unitPriceMinor, setUnitPriceMinor] = useState(Number(pricing.unitPrice || 0));
  const [totalPriceMinor, setTotalPriceMinor] = useState(Number(pricing.totalPrice || 0));
  const [saving, setSaving] = useState(false);
  const updateOperationFields = useOperationStore((state) => state.updateOperationFields);

  const estimatedVolumeNumber = Number(estimatedVolume || 0);
  const selectedCurrencyMeta = getCurrencyMeta(currency);
  const selectedSymbol = currencyLabels[currency] || selectedCurrencyMeta.symbol || currency;
  const totalOffer = pricingType === 'PER_M3'
    ? Number((estimatedVolumeNumber * Number(unitPriceMinor || 0)).toFixed(2))
    : Number(totalPriceMinor || 0);
  const displayPrice = pricingType === 'PER_M3' ? Number(unitPriceMinor || 0) : Number(totalPriceMinor || 0);

  const handlePricingTypeChange = (nextType) => {
    if (nextType === pricingType) return;

    if (nextType === 'PER_M3') {
      const nextUnitPrice = estimatedVolumeNumber > 0
        ? Number(((Number(totalPriceMinor || 0) || 0) / estimatedVolumeNumber).toFixed(2))
        : Number(unitPriceMinor || 0);
      setUnitPriceMinor(nextUnitPrice);
    } else {
      setTotalPriceMinor(Number((estimatedVolumeNumber * Number(unitPriceMinor || 0)).toFixed(2)));
    }

    setPricingType(nextType);
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    const payload = {
      surveyorName,
      estimatedVolume: estimatedVolumeNumber,
      measuredM3: estimatedVolumeNumber,
      pricingType,
      unitPrice: pricingType === 'PER_M3'
        ? Number(unitPriceMinor || 0)
        : estimatedVolumeNumber > 0
          ? Number((totalOffer / estimatedVolumeNumber).toFixed(2))
          : 0,
      totalPrice: totalOffer,
      currency,
      pricePerM3: pricingType === 'PER_M3'
        ? Number(unitPriceMinor || 0)
        : estimatedVolumeNumber > 0
          ? Number((Number(totalPriceMinor || 0) / estimatedVolumeNumber).toFixed(2))
          : 0,
      pricingAmount: totalOffer,
      priceCurrency: currency
    };

    const result = await updateOperationFields(operation._id, payload);
    if (result.success) toast.success('Keşif ve fiyatlandırma kaydedildi.');
    else toast.error(result.message);
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm lg:col-span-7">
      <fieldset disabled={!canWrite || saving}>
        <h2 className="mb-4 text-lg font-black">Fiyatlandırma</h2>

        <div className="space-y-5">
          <label className="font-bold">
            Öngörülen Hacim (m³)
            <NumericFormat
              className={inputClass}
              value={estimatedVolume}
              inputMode="decimal"
              thousandSeparator="."
              decimalSeparator=","
              allowedDecimalSeparators={[',', '.']}
              decimalScale={2}
              fixedDecimalScale={false}
              allowNegative={false}
              placeholder="4,50"
              onFocus={(event) => event.target.select()}
              onValueChange={({ value }, source) => {
                if (source.source === 'event') setEstimatedVolume(value);
              }}
            />
          </label>

          <div>
            <p className="font-bold">Fiyatlandırma Tipi</p>
            <div className="mt-2 inline-flex rounded-2xl bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => handlePricingTypeChange('PER_M3')}
                className={`rounded-xl px-4 py-2 text-xs font-black transition-all ${
                  pricingType === 'PER_M3'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                m³ Başına Birim Fiyat
              </button>
              <button
                type="button"
                onClick={() => handlePricingTypeChange('FLAT_RATE')}
                className={`rounded-xl px-4 py-2 text-xs font-black transition-all ${
                  pricingType === 'FLAT_RATE'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Toplam Götürü Fiyat
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="font-bold">
              Para Birimi
              <select className={inputClass} value={currency} onChange={(event) => setCurrency(event.target.value)}>
                {PRICE_CURRENCIES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>

            <label className="font-bold">
              {pricingType === 'PER_M3'
                ? `m³ Başına Fiyat (${selectedSymbol})`
                : `Toplam Fiyat (${selectedSymbol})`}
              <NumericFormat
                required
                className={inputClass}
                value={displayPrice}
                inputMode="decimal"
                thousandSeparator="."
                decimalSeparator=","
                allowedDecimalSeparators={[',', '.']}
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                placeholder="0,00"
                onFocus={(event) => event.target.select()}
                onValueChange={({ floatValue }, source) => {
                  if (source.source !== 'event') return;
                  const nextValue = Number(floatValue || 0);
                  if (pricingType === 'PER_M3') {
                    setUnitPriceMinor(nextValue);
                  } else {
                    setTotalPriceMinor(nextValue);
                  }
                }}
              />
            </label>
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-500">Öngörülen Toplam Teklif Fiyatı</p>
            <p className="mt-2 text-2xl font-black text-blue-700">{formatMoney(totalOffer, currency)}</p>
          </div>
        </div>

        <section className="mt-5 border-t border-slate-100 pt-5">
          <h2 className="mb-3 text-lg font-black">Keşif Bilgileri</h2>
          <label className="font-bold">
            Keşif Yapan
            <input className={inputClass} value={surveyorName} onChange={(event) => setSurveyorName(event.target.value)} placeholder="Ad Soyad" />
          </label>
        </section>

        {canWrite && <button className="mt-4 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white">{saving ? 'Kaydediliyor...' : 'Keşif ve Fiyatlandırmayı Kaydet'}</button>}
      </fieldset>
    </form>
  );
};

export default OperationSurveyPricingCard;
